import * as Sentry from '@sentry/browser'
import { sanitizeSentryUrl } from '~/utils/sentrySanitization'
import { getOrganizationAccessRecoveryPath } from '~/utils/subscriptionAccess'

const reportedErrors = new WeakSet()
const EXPECTED_HTTP_STATUSES = [401, 422]

const safeEndpoint = url => {
	if (typeof url !== 'string') {
		return null
	}

	return sanitizeSentryUrl(url.split('?')[0])
}

const tagValue = value =>
	value === null || typeof value === 'undefined' ? 'unknown' : String(value)

const sanitizedWorkflowError = ({ flow, operation, status }) => {
	const workflow = flow || 'unknown'
	const action = operation || 'unknown'
	const outcome = status ? `HTTP ${status}` : 'network error'
	const error = new Error(
		`Job offer workflow failed: ${workflow}/${action} (${outcome})`
	)
	error.name = 'JobOfferWorkflowError'

	return error
}

export const reportWorkflowError = (
	error,
	{
		flow,
		operation,
		rootState = {},
		jobOfferId = null,
		teamAssessmentMode = false,
		reportValidationError = false
	} = {}
) => {
	if (!process.client || !error || typeof error !== 'object') {
		return
	}

	const status = error.response?.status ?? null
	if (
		EXPECTED_HTTP_STATUSES.includes(status) &&
		!(status === 422 && reportValidationError)
	) {
		return
	}

	if (reportedErrors.has(error)) {
		return
	}
	reportedErrors.add(error)

	const user = rootState.user?.user || null
	const organization = user?.current_organization || null
	const responseData = error.response?.data || {}
	const capturedError = sanitizedWorkflowError({ flow, operation, status })
	const context = {
		flow: flow || 'unknown',
		operation: operation || 'unknown',
		endpoint: safeEndpoint(error.config?.url),
		method: error.config?.method?.toUpperCase?.() || null,
		http_status: status,
		job_offer_id: jobOfferId,
		organization_id: organization?.id ?? null,
		user_id: user?.id ?? null,
		organization_role: organization?.role_in_organization ?? null,
		account_verified:
			typeof user?.verified === 'boolean' ? user.verified : null,
		team_assessment_mode: Boolean(teamAssessmentMode),
		payment_status:
			responseData.payment_status ?? organization?.payment_status ?? null,
		trial_expired:
			responseData.trial_expired ?? organization?.trial_expired ?? null,
		subscription_expired: responseData.subscription_expired ?? null,
		requires_payment: responseData.requires_payment ?? null
	}

	Sentry.withScope(scope => {
		// These synthetic reports deliberately carry only the context below.
		// Inherited XHR/console breadcrumbs can retain invitation URLs or raw
		// Axios errors, including request payloads.
		scope.clearBreadcrumbs?.()
		scope.setLevel(status && status < 500 ? 'warning' : 'error')
		scope.setTag('feature', 'job_offer_workflow')
		scope.setTag('workflow', tagValue(flow))
		scope.setTag('operation', tagValue(operation))
		scope.setTag('http_status', tagValue(status))
		scope.setTag(
			'organization_role',
			tagValue(organization?.role_in_organization)
		)
		scope.setTag('account_verified', tagValue(user?.verified))
		scope.setTag(
			'payment_status',
			tagValue(
				responseData.payment_status ?? organization?.payment_status
			)
		)
		scope.setContext('job_offer_workflow', context)
		Sentry.captureException(capturedError)
	})
}

export const runWorkflowRequest = async ({
	request,
	redirect = null,
	recoverOrganizationAccess = true,
	ignoredHttpStatuses = [],
	...reportContext
}) => {
	try {
		return await request()
	} catch (error) {
		if (!ignoredHttpStatuses.includes(error.response?.status)) {
			reportWorkflowError(error, reportContext)
		}

		const recoveryPath = recoverOrganizationAccess
			? getOrganizationAccessRecoveryPath(error)
			: null

		if (recoveryPath && typeof redirect === 'function') {
			redirect(recoveryPath)
			return null
		}

		throw error
	}
}
